import { gql, useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Input, Select, Switcher, Text } from "components/base/Form";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useEffect, useState } from "react";
import { v4 } from "uuid";
import ShippingMarkPreview from "./Preview";

const FETCH_PRODUCT_INFO_FOR_SHIPPING_MARK = gql`
  query FETCH_PRODUCT_INFO_FOR_SHIPPING_MARK($id: ID!, $customerId: ID!) {
    product(id: $id) {
      id
      shippingMarkName
      number
      outerCartonBarcodeEan
      outerCartonBarcodeUpc
      outerCartonX
      outerCartonZ
      outerCartonY
      innerCartonX
      innerCartonY
      innerCartonZ
      quantityPerCarton
      ctnNetWeight
      ctnGrossWeight
      packing
      colors
      shippingMarkNotes: computedShippingMarkNotes
      shippingMarkSettings {
        id
        customer {
          id
        }
        options
      }
    }
    customer(id: $customerId) {
      id
      name
      shippingZone
    }
  }
`;

const SAVE_SHIPPING_MARK_SETTINGS = gql`
  mutation SAVE_SHIPPING_MARK_SETTINGS(
    $productId: ID!
    $customerId: ID
    $options: String!
  ) {
    saveShippingMarkSettings(
      productId: $productId
      customerId: $customerId
      options: $options
    ) {
      shippingMarkSettings {
        id
      }
    }
  }
`;

const EU_VALUES = {
  main: [
    { key: "qty", prefix: "QTY: ", suffix: " PCS" },
    { key: "item", prefix: "ITEM: ", suffix: "" },
    { key: "barcode", prefix: "BARCODE: ", suffix: "" },
    { key: "detectedtype", prefix: "DETECTED BARCODE TYPE: ", suffix: "" },
    { key: "color", prefix: "COLOR: ", suffix: "" },
    { key: "nw", prefix: "N.W.: ", suffix: " KG" },
    { key: "gw", prefix: "G.W.: ", suffix: " KG" },
    { key: "dims", prefix: "DIMS: ", suffix: " CM" },
    { key: "ctn", prefix: "CTN NO.: ", suffix: "" },
  ],
  side: [
    { key: "item", prefix: "", suffix: "" },
    { key: "barcode", prefix: "BARCODE: ", suffix: "" },
    { key: "qty", prefix: "", suffix: " PCS" },
    { key: "gw", prefix: "G.W.: ", suffix: " KG" },
  ],
};

const US_VALUES = {
  main: [
    { key: "item", prefix: "", suffix: "" },
    {
      key: "distribute",
      prefix: "",
      value: "DISTRIBUTED BY WABOBA INC",
      suffix: "",
    },
    { key: "dest", prefix: "DEST: ", value: "LITHIA SPRINGS, GA", suffix: "" },
    { key: "number", prefix: "ITEM # ", suffix: "" },
    { key: "barcode", prefix: "BARCODE: ", suffix: "" },
    { key: "detectedtype", prefix: "DETECTED BARCODE TYPE: ", suffix: "" },
    { key: "qty", prefix: "", suffix: " PCS" },
    { key: "dims", prefix: "CARTON DIMS: ", suffix: " CM" },
    { key: "nw", prefix: "N.W.: ", suffix: " KG" },
    { key: "gw", prefix: "G.W.: ", suffix: " KG" },
    { key: "ctn", prefix: "CARTON NO.: ", suffix: "" },
    { key: "made", prefix: "", value: "MADE IN CHINA", suffix: "" },
  ],
  side: [
    { key: "item", prefix: "", suffix: "" },
    { key: "number", prefix: "ITEM#", suffix: "" },
    { key: "barcode", prefix: "BARCODE: ", suffix: "" },
    { key: "qty", prefix: "", suffix: " PCS" },
    { key: "gw", prefix: "G.W.: ", suffix: " KG" },
  ],
};

const templates = {
  EU: EU_VALUES,
  US: US_VALUES,
};

const EMPTY_NUMBER_VALUE = "\u00a0".repeat(12);
const trimValue = (value) => (typeof value === "string" ? value.trim() : value);
const markNumber = (value) =>
  Number(value) === 0 ? EMPTY_NUMBER_VALUE : trimValue(value);

const GenerateShippingMark = ({ customer, row }) => {
  const { loading, error, data } = useQuery(
    FETCH_PRODUCT_INFO_FOR_SHIPPING_MARK,
    {
      variables: { id: row.product.id, customerId: customer.id },
      fetchPolicy: "network-only",
    },
  );

  // default settings
  const [template, setTemplate] = useState("");
  const [newLogo, setNewLogo] = useState(true); // only use new logo from now on
  const [rotate, setRotate] = useState(false);
  const [mainFontScale, setMainFontScale] = useState(100);
  const [sideFontScale, setSideFontScale] = useState(100);
  const [moveUp, setMoveUp] = useState(false);

  const [itemNumber, setItemNumber] = useState(row.itemNumber);
  const [values, setValues] = useState(US_VALUES);
  const [notes, setNotes] = useState("");
  const [detectedType, setDetectedType] = useState("");
  const [innerCartonPcs, setInnerCartonPcs] = useState(0);
  const [dimensions, setDimensions] = useState({ x: "", z: "", y: "" });

  const [saveShippingMarkSettings] = useMutation(SAVE_SHIPPING_MARK_SETTINGS, {
    onCompleted() {
      Alert("success", "Settings Saved.");
    },
    onError(error) {
      Alert("error", error.message);
    },
  });

  function saveSettings(changes) {
    saveShippingMarkSettings({
      variables: {
        productId: row.product.id,
        customerId: customer.id,
        options: JSON.stringify({
          newLogo,
          rotate,
          mainFontScale,
          sideFontScale,
          moveUp,
          ...changes,
        }),
      },
    });
  }

  function mapValues(i, data, currentTemplate) {
    if (i.key === "qty") return { ...i, value: row.qtyPerCarton };
    if (i.key === "item")
      return {
        ...i,
        value: data.product.shippingMarkName
          ? data.product.shippingMarkName
          : row.packingName,
      };
    if (i.key === "number")
      return {
        ...i,
        value:
          data.product.number === "101C01_DS12"
            ? "101C01_DS"
            : data.product.number,
      };
    if (i.key === "barcode")
      return {
        ...i,
        prefix: "BARCODES: ",
        value:
          currentTemplate === "US"
            ? data.product.outerCartonBarcodeUpc
              ? data.product.outerCartonBarcodeUpc.replaceAll(" ", "")
              : ""
            : data.product.outerCartonBarcodeEan
              ? data.product.outerCartonBarcodeEan.replaceAll(" ", "")
              : "",
      };
    if (i.key === "color") return { ...i, value: data.product.colors || "" };
    if (i.key === "nw") return { ...i, value: row.netWeight };
    if (i.key === "gw") return { ...i, value: row.grossWeight };
    if (i.key === "dims")
      return {
        ...i,
        value: `${data.product.outerCartonX} x ${data.product.outerCartonZ} x ${data.product.outerCartonY}`,
      };
    if (i.key === "ctn") {
      return { ...i, value: `______ of ${row.totalCarton}` };
    }
    return i;
  }

  useEffect(() => {
    if (data) {
      const shippingZone = data.customer?.shippingZone;
      if (!templates[shippingZone]) return;

      if (data.product.quantityPerCarton === 72) {
        setInnerCartonPcs(24); // used to display mark for each inner carton
      }

      const prevSettings = data.product.shippingMarkSettings.find(
        (i) => i.customer.id === customer.id,
      );
      const settingOptions = prevSettings
        ? JSON.parse(prevSettings.options)
        : null;

      const currentTemplate = shippingZone;
      // const currentTemplate = settingOptions ? settingOptions.template : "EU";
      setTemplate(currentTemplate);
      setDimensions({
        x: data.product.outerCartonX,
        z: data.product.outerCartonZ,
        y: data.product.outerCartonY,
      });

      if (settingOptions) {
        // setNewLogo(settingOptions.newLogo || false);
        setRotate(settingOptions.rotate);
        setMainFontScale(settingOptions.mainFontScale);
        setSideFontScale(settingOptions.sideFontScale);
        setMoveUp(settingOptions.moveUp);
      }

      setValues({
        main: templates[currentTemplate].main.map((i) =>
          mapValues(i, data, currentTemplate),
        ),
        side: templates[currentTemplate].side.map((i) =>
          mapValues(i, data, currentTemplate),
        ),
      });

      if (data.product.shippingMarkNotes)
        setNotes(data.product.shippingMarkNotes);
    }
  }, [data]);

  useEffect(() => {
    const currentBarcode = trimValue(
      values.main.find((i) => i.key === "barcode").value,
    );
    if (currentBarcode) {
      if (currentBarcode.length === 12) {
        setDetectedType("upc");
      } else if (currentBarcode.length === 13) {
        setDetectedType("ean13");
      } else if (currentBarcode.length === 14) {
        setDetectedType("itf");
      } else {
        setDetectedType("unknown");
      }
    } else setDetectedType("");
  }, [values]);

  // function onChangeTemplate(newTemplate) {
  //   setTemplate(newTemplate);
  //   setValues({
  //     main: templates[newTemplate].main.map((i) =>
  //       mapValues(i, data, newTemplate),
  //     ),
  //     side: templates[newTemplate].side.map((i) =>
  //       mapValues(i, data, newTemplate),
  //     ),
  //   });
  // }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;
  const shippingZone = data.customer?.shippingZone;
  if (!templates[shippingZone]) {
    return (
      <Errors
        error={{
          message:
            "Customer Shipping Zone is required. Please set it to EU or US before generating Shipping Mark.",
        }}
      />
    );
  }

  const x = parseFloat(data.product.outerCartonX);
  const y = parseFloat(data.product.outerCartonY);
  const z = parseFloat(data.product.outerCartonZ);
  const innerX = parseFloat(data.product.innerCartonX) / 10; // mm -> cm
  const innerY = parseFloat(data.product.innerCartonY) / 10;
  const innerZ = parseFloat(data.product.innerCartonZ) / 10;

  const item = trimValue(values.main.find((i) => i.key === "item").value);
  const barcode = trimValue(values.main.find((i) => i.key === "barcode").value);
  const previewItemNumber = trimValue(itemNumber);
  const previewNotes = trimValue(notes);
  const previewValue = (i) => {
    if (["nw", "gw"].includes(i.key)) return markNumber(i.value);
    if (i.key === "dims")
      return [dimensions.x, dimensions.z, dimensions.y]
        .map(markNumber)
        .join(" x ");
    return trimValue(i.value);
  };
  const computedValues = {
    main: values.main
      .filter((i) => i.key !== "detectedtype")
      .map((i) => ({ ...i, value: previewValue(i) })),
    side: values.side
      .filter((i) => i.key !== "detectedtype")
      .map((i) => ({ ...i, value: previewValue(i) })),
  };
  // console.log('computedValues', computedValues);

  function addRow(face, index) {
    const newValues = { ...values };
    newValues[face].splice(index + 1, 0, {
      key: v4(),
      prefix: "",
      value: "",
      suffix: "",
      tmp: true,
    });
    setValues(newValues);
  }

  function removeRow(face, index) {
    const newValues = { ...values };
    newValues[face].splice(index, 1);
    setValues(newValues);
  }

  function onChangeValue(key, value) {
    setValues((prev) => ({
      main: prev.main.map((i) => (key === i.key ? { ...i, value } : i)),
      side: prev.side.map((i) => (key === i.key ? { ...i, value } : i)),
    }));
  }

  return (
    <div className="flex gap-8">
      <div className="w-1/3 flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <h2 className="text-base">Main Face</h2>
          <div className="bg-gray-100 p-4 rounded-xl">
            {template === "EU" ? (
              <div className="flex w-full items-center gap-3 py-2">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span className="w-24 shrink-0" />
                  <Input
                    className="w-full"
                    value={itemNumber}
                    onChange={(e) => setItemNumber(e.target.value)}
                  />
                </div>
                <div className="ml-2 flex shrink-0 items-center gap-3">
                  <Button onClick={() => addRow("main", -1)}>+</Button>
                </div>
              </div>
            ) : null}

            {values.main.map((i, index) =>
              i.key === "detectedtype" ? null : (
                <div
                  key={i.key}
                  className="flex w-full items-center gap-3 py-2"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="w-24 shrink-0 opacity-60">{i.prefix}</span>
                    {i.key === "dims" ? (
                      <div className="flex flex-1 items-center gap-1">
                        <Input
                          aria-label="Dimensions X"
                          className="w-16 text-center"
                          type="number"
                          value={dimensions.x}
                          onChange={(e) =>
                            setDimensions((prev) => ({
                              ...prev,
                              x: e.target.value,
                            }))
                          }
                        />
                        <span>×</span>
                        <Input
                          aria-label="Dimensions Z"
                          className="w-16 text-center"
                          type="number"
                          value={dimensions.z}
                          onChange={(e) =>
                            setDimensions((prev) => ({
                              ...prev,
                              z: e.target.value,
                            }))
                          }
                        />
                        <span>×</span>
                        <Input
                          aria-label="Dimensions Y"
                          className="w-16 text-center"
                          type="number"
                          value={dimensions.y}
                          onChange={(e) =>
                            setDimensions((prev) => ({
                              ...prev,
                              y: e.target.value,
                            }))
                          }
                        />
                      </div>
                    ) : (
                      <Input
                        className={
                          i.key === "barcode"
                            ? "flex-1 min-w-0"
                            : ["qty", "nw", "gw"].includes(i.key)
                              ? "w-24 text-center"
                              : "w-full"
                        }
                        type={
                          ["qty", "nw", "gw"].includes(i.key)
                            ? "number"
                            : "text"
                        }
                        value={i.value}
                        onChange={(e) => onChangeValue(i.key, e.target.value)}
                        placeholder={i.tmp ? null : i.key}
                      />
                    )}
                    {i.key === "barcode" ? (
                      <>
                        {detectedType === "ean13" ? (
                          <span>EAN 13</span>
                        ) : detectedType === "upc" ? (
                          <span>UPC</span>
                        ) : detectedType === "itf" ? (
                          <span>GTIN 14</span>
                        ) : (
                          <span className="text-red-500">Invalid barcode</span>
                        )}
                      </>
                    ) : null}
                    {i.suffix ? (
                      <span className="opacity-60">{i.suffix}</span>
                    ) : null}
                  </div>
                  <div className="ml-2 flex shrink-0 items-center gap-3">
                    {i.tmp ? (
                      <Button onClick={() => removeRow("main", index)} danger>
                        -
                      </Button>
                    ) : null}
                    <Button onClick={() => addRow("main", index)}>+</Button>
                  </div>
                </div>
              ),
            )}
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <h2 className="text-base">Settings</h2>
          <div className="bg-gray-100 p-4 rounded-xl">
            <table className="w-auto">
              <tbody>
                <tr>
                  <td className="opacity-60">Template</td>
                  <td>{shippingZone}</td>
                </tr>
                <tr>
                  <td>
                    <span className="opacity-60">Rotate: </span>
                  </td>
                  <td>
                    <Switcher
                      isOn={rotate}
                      onChange={() => {
                        const next = !rotate;
                        setRotate(next);
                        saveSettings({ rotate: next });
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="opacity-60">Main Face Scale:</span>
                  </td>
                  <td>
                    <Select
                      value={`${mainFontScale}`}
                      onChange={(e) => {
                        setMainFontScale(e.target.value);
                        saveSettings({ mainFontScale: e.target.value });
                      }}
                    >
                      {[...Array(300).keys()].map((i) => (
                        <option key={i} value={i + 1}>
                          {i + 1}%
                        </option>
                      ))}
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="opacity-60">Main Face Move Up:</span>
                  </td>
                  <td>
                    <Switcher
                      isOn={moveUp}
                      onChange={() => {
                        const next = !moveUp;
                        setMoveUp(next);
                        saveSettings({ moveUp: next });
                      }}
                    />
                  </td>
                </tr>
                {values.side.length > 0 ? (
                  <tr>
                    <td>
                      <span className="opacity-60">Side Face Scale:</span>
                    </td>
                    <td>
                      <Select
                        value={`${sideFontScale}`}
                        onChange={(e) => {
                          setSideFontScale(e.target.value);
                          saveSettings({ sideFontScale: e.target.value });
                        }}
                      >
                        {[...Array(300).keys()].map((i) => (
                          <option key={i} value={i + 1}>
                            {i + 1}%
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        {values.side.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h2 className="text-base">Side Face</h2>
            <div className="bg-gray-100 p-4 rounded-xl">
              {values.side.map((i, index) => (
                <div
                  key={i.key}
                  className="flex w-full items-center gap-3 py-2"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    {i.key === "detectedtype" ? (
                      <>
                        <span className="opacity-60">{i.prefix}</span>
                        {detectedType === "ean13" ? (
                          <span>EAN 13</span>
                        ) : detectedType === "upc" ? (
                          <span>UPC</span>
                        ) : detectedType === "itf" ? (
                          <span>GTIN 14</span>
                        ) : (
                          <span className="text-red-500">Invalid barcode</span>
                        )}
                      </>
                    ) : i.tmp ? (
                      <Input
                        className="w-full"
                        value={i.value}
                        onChange={(e) => onChangeValue(i.key, e.target.value)}
                      />
                    ) : (
                      <>
                        {i.prefix ? (
                          <span className="opacity-60">{i.prefix}</span>
                        ) : null}
                        <span>{i.value}</span>
                        {i.suffix ? (
                          <span className="opacity-60">{i.suffix}</span>
                        ) : null}
                      </>
                    )}
                  </div>
                  <div className="ml-2 flex shrink-0 items-center gap-3">
                    {i.tmp ? (
                      <Button onClick={() => removeRow("side", index)} danger>
                        -
                      </Button>
                    ) : null}
                    <Button onClick={() => addRow("side", index)}>+</Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="flex flex-col gap-2">
          <h2 className="text-base">Notes</h2>
          <Text
            placeholder="Write some notes here together in the PDF."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </section>

        <details className="flex flex-col gap-2">
          <summary className="cursor-pointer text-base">Notice</summary>
          <ul className="leading-relaxed list-decimal list-inside">
            <li>
              该功能暂不支持打印内箱的唛头图稿，有内箱的产品请单独与工厂沟通唛头资料。
            </li>
            <li>
              该功能暂时不支持客制版的唛头图稿，客制版的唛头按照旧流程操作。
            </li>
            <li>
              唛头图稿出现有文字超出版面或者文字盖住条形码、logo的情况，需要调整印刷字体的百分比，然后按“save
              setting"保存设置。
            </li>
            <li>
              带隔卡的产品在唛头上会出现“外箱需要配套K4K材质的隔卡，请记得安排生产！”字眼，下单时需提醒九州配套隔卡生产。
            </li>
            <li>
              Heads
              glide三个产品的唛头需要做转向设置，按“Rotate"按钮把唛头转向并检查唛头是否需要做字体大小的调整，选择了合适的百分比之后按“Save
              Setting"为该产品保存转向设置和百分比设置。
            </li>
            <li>产品101C01_DS12的唛头要求印刷101C02_DS这个Item#。</li>
            <li>
              对于Formula
              sports使用欧洲唛头模板的客户，产品321C99_A48要求使用UPC条形码。
            </li>
            <li>
              下载唛头图稿之前务必检查唛头上的产品信息并确保图稿没有印刷问题再发给工厂。
            </li>
            <li>
              条码类型将根据当前输入的条码值显示对应类型，生成右侧一次性的唛头。
            </li>
            <li>
              出货到ALC仓库的wingman UFO需要在外箱贴一张UN3091防火标,
              贴在侧唛的任意一面即可，每个外箱只需要贴一张。
            </li>
          </ul>
        </details>
      </div>

      <div className="flex-1 flex flex-col gap-2">
        <h2 className="text-base">Mark Preview</h2>
        <div className="flex flex-1 flex-col gap-4">
          <div className="bg-gray-100 dark:bg-gray-900 rounded-xl flex-1 flex flex-col relative overflow-auto p-8">
            {item && (
              <ShippingMarkPreview
                variables={{
                  item,
                  x,
                  y,
                  z,
                  barcode,
                  barcodeType: detectedType,
                  newLogo,
                  template,
                  itemNumber: previewItemNumber,
                  values: computedValues,
                  notes: previewNotes,
                  rotate,
                  mainFontScale: parseFloat(mainFontScale) / 100,
                  sideFontScale: parseFloat(sideFontScale) / 100,
                  moveUp,
                }}
              />
            )}
          </div>

          {template === "EU" && innerCartonPcs !== 0 && (
            <div className="bg-gray-100 dark:bg-gray-900 rounded-xl flex-1 flex flex-col relative overflow-auto p-8">
              <ShippingMarkPreview
                variables={{
                  item,
                  x: innerX,
                  y: innerY,
                  z: innerZ,
                  barcode,
                  barcodeType: detectedType,
                  newLogo,
                  template,
                  itemNumber: previewItemNumber,
                  values: computedValues,
                  notes: previewNotes,
                  rotate,
                  mainFontScale: parseFloat(mainFontScale) / 100,
                  sideFontScale: parseFloat(sideFontScale) / 100,
                  moveUp,
                  innerCartonPcs,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateShippingMark;
